import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AppConfig } from '../app-config.loader';
import { LoaderExclusionType, LoaderService } from '../layout/loader/loader.service';
import { CisAllegato, CisAllegatoRequest, CisClassificaUpdateAllegatoRequest, CisUpdateAllegatoRequest } from '../shared/models/cisAllegato.model';
import { AllegatiCondivisiDetails, AllegatiCondivisiRequest, DocumentoAllegato, TipoAllegato } from '../shared/models/documentoAllegato.model';
import { ScanRequest } from '../shared/models/pending-scan.model';

@Injectable({
  providedIn: 'root'
})
export class AllegatiService {
  private apiBaseUrl: string;

  constructor(appConfig: AppConfig, private http: HttpClient) {
    this.apiBaseUrl = `${appConfig.settings.apiRoot}`;
    // La scansione può restare in attesa per alcuni minuti. Il relativo stato è
    // mostrato nella modale e non deve attivare l'overlay globale della pagina.
    LoaderService.addExclusion('allegati/acquireimage', LoaderExclusionType.isContained);
    LoaderService.addExclusion('allegati/pendingscan', LoaderExclusionType.isContained);
  }

  public getAllegati(docId: number, includeSharedDocs: boolean = false, tipoAllegato?: TipoAllegato): Observable<DocumentoAllegato[]> {
    let params: HttpParams = new HttpParams().set('includeSharedDocuments', includeSharedDocs);
    if (typeof tipoAllegato !== 'undefined') {
      params = params.set('tipoAllegato', tipoAllegato);
    }

    return this.http.get<DocumentoAllegato[]>(`${this.apiBaseUrl}/allegati/documento/${docId}`, {params: params});
  }

  public getAllegatiCIS(docId: number): Observable<CisAllegato[]> {
    return this.http.get<CisAllegato[]>(`${this.apiBaseUrl}/allegati/cis/documento/${docId}`);
  }

  public insertAllegatoCIS(allegato: CisAllegatoRequest): Observable<unknown> {
    return this.http.post(`${this.apiBaseUrl}/allegati/cis/addallegato`, allegato);
  }

  public updateAllegatoCIS(allegato: CisUpdateAllegatoRequest): Observable<unknown> {
    return this.http.put(`${this.apiBaseUrl}/allegati/cis/updateallegato`, allegato);
  }

  public updateClassificaAllegatoCIS(allegato: CisClassificaUpdateAllegatoRequest): Observable<unknown> {
    return this.http.put(`${this.apiBaseUrl}/allegati/cis/updateallegato/classifica`, allegato);
  }

  public deleteAllegato(allegato: DocumentoAllegato): Observable<unknown> {
    return this.http.delete(`${this.apiBaseUrl}/allegati/${allegato.streamId}`);
  }

  public updateRiproduzione(allegato: DocumentoAllegato): Observable<unknown> {
    return this.http.put(`${this.apiBaseUrl}/allegati/updateRiproduzione/${allegato.streamId}`, allegato.riproduzione);
  }

  public setPrimaryAllegato(allegato: DocumentoAllegato): Observable<unknown> {
    return this.http.put(`${this.apiBaseUrl}/allegati/${allegato.streamId}/primario`, null);
  }

  // public downloadAllegato(allegato: DocumentoAllegato): void {
  //   this.http.get(`${this.apiBaseUrl}/allegati/${allegato.streamId}`, {
  //     responseType: 'blob',
  //     observe: 'events',
  //     reportProgress: true
  //   }).subscribe(event => {
  //     if (event.type === HttpEventType.Response) {
  //       const res = event as HttpResponse<Blob>;
  //       if (!res.body) return;
  //       const blobUrl = window.URL.createObjectURL(res.body);
  //       const link = document.createElement('a');
  //       if (!allegato.fileName && res.headers.get('X-Filename')) {
  //         allegato.fileName = res.headers.get('X-Filename') ?? '';
  //       }

  //       link.download = allegato.fileName;
  //       link.href = blobUrl;
  //       link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
  //       setTimeout(() => {
  //         window.URL.revokeObjectURL(blobUrl);
  //         link.remove();
  //       }, 100);
  //     }
  //   });
  // }
  public downloadAllegato(allegato: DocumentoAllegato): void {
    const targetFileName = this.getTargetFileName(allegato, false);
    this.http.get<Blob>(this.getAllegatoUrl(allegato, false), {
      responseType: 'blob' as 'json',
      observe: 'response'
    }).subscribe(res => {
      this.downloadFromResponse(targetFileName, res);
    });
  }
  public openAllegato(allegato: DocumentoAllegato): void {
    const targetFileName = this.getTargetFileName(allegato, false);
    this.http.get<Blob>(this.getAllegatoUrl(allegato, false), {
      responseType: 'blob' as 'json',
      observe: 'response'
    }).subscribe(res => {
      this.decodeAllegato(targetFileName, res);
    });
  }
  public downloadAllegatoOriginale(allegato: DocumentoAllegato): void {
    const targetFileName = this.getTargetFileName(allegato, true);
    this.http.get<Blob>(this.getAllegatoUrl(allegato, true), {
      responseType: 'blob' as 'json',
      observe: 'response'
    }).subscribe(res => {
      this.downloadFromResponse(targetFileName, res);
    });
  }

  public getPassword(allegato: DocumentoAllegato): Observable<{ password: string }> {
    return this.http.get<{ password: string }>(`${this.apiBaseUrl}/allegati/${allegato.streamId}/password`);
  }

  public acquireImage(docId: number): Observable<ScanRequest> {
    return this.http.get<ScanRequest>(`${this.apiBaseUrl}/allegati/acquireImage/${docId}`);
  }

  public downloadPendingScan(scanRequestId: string): Observable<HttpResponse<Blob>> {
    return this.http.get<Blob>(`${this.apiBaseUrl}/allegati/pendingScan/${scanRequestId}/file`, {
      responseType: 'blob' as 'json',
      observe: 'response'
    });
  }

  public deletePendingScan(scanRequestId: string): Observable<unknown> {
    return this.http.delete(`${this.apiBaseUrl}/allegati/pendingScan/${scanRequestId}`);
  }

  public startOCR(allegato: DocumentoAllegato): Observable<unknown> {
    return this.http.put(`${this.apiBaseUrl}/allegati/${allegato.streamId}/relaunchocr`, null);
  }

  public modifyOCR(allegato: DocumentoAllegato): Observable<unknown> {
    const ocrTesto = {
      ocrTesto: allegato.ocrTesto
    };
    return this.http.put(`${this.apiBaseUrl}/allegati/${allegato.streamId}/setocrtesto`, ocrTesto);
  }

  public getOCR(streamId: string): Observable<string> {
    return this.http.get<string>(`${this.apiBaseUrl}/allegati/${streamId}/ocr`, { responseType: 'text' as 'json' });
  }

  public getAllegatoPreview(fileName: string): void {
    this.http.get<Blob>(`${this.apiBaseUrl}/allegati/documento/condivisi/preview/${fileName}`, {
      responseType: 'blob' as 'json',
      observe: 'response'
    }).subscribe(res => {
      this.decodeAllegato(fileName, res);
    });
  }

  public uploadAllegatiCondivisi(docId: number, files: AllegatiCondivisiRequest): Observable<unknown> {
    return this.http.post(`${this.apiBaseUrl}/allegati/upload/condivisi/${docId}`, files);
  }

  private getAllegatoUrl(allegato: DocumentoAllegato, original: boolean): string {
    if (!original && allegato.isP7m) {
      return `${this.apiBaseUrl}/allegati/${allegato.streamId}/estratto`;
    }

    return `${this.apiBaseUrl}/allegati/${allegato.streamId}`;
  }

  private getTargetFileName(allegato: DocumentoAllegato, original: boolean): string {
    if (!original && allegato.isP7m && allegato.extractedFileName) {
      return allegato.extractedFileName;
    }

    return allegato.fileName;
  }

  private decodeAllegato(fileName: string, content: HttpResponse<Blob>): void {
    if (!content.body) return;

    const resolvedFileName = content.headers.get('X-Filename') ?? fileName;
    let documentType: string;
      if (resolvedFileName.toLowerCase().endsWith('pdf')) {
        documentType = 'application/pdf';
      } else if (resolvedFileName.toLowerCase().endsWith('doc')) {
        documentType = 'application/msword';
      } else if (resolvedFileName.toLowerCase().endsWith('docx')){
        documentType =  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      } else if (resolvedFileName.toLowerCase().endsWith('pptx')){
        documentType =  'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      } else if (resolvedFileName.toLowerCase().endsWith('ppt')){
        documentType =  'application/vnd.ms-powerpoint';
      } else if (resolvedFileName.toLowerCase().endsWith('odt')){
        documentType =  'application/vnd.oasis.opendocument.text';
      } else if (resolvedFileName.toLowerCase().endsWith('xlsx')){
        documentType =  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      } else if (resolvedFileName.toLowerCase().endsWith('xls')){
        documentType =  'application/vnd.ms-excel';
      } else {
        documentType = 'text/plain';
      }

      const file = new File([content.body], resolvedFileName, { type: documentType })
      const url = window.URL.createObjectURL(file);

      window.open(url, '_blank', 'noreferrer');
  }

  private downloadFromResponse(fileName: string, res: HttpResponse<Blob>): void {
    if (!res.body) return;

    const resolvedFileName = res.headers.get('X-Filename') ?? fileName;
    const blobUrl = window.URL.createObjectURL(res.body);
    const link = document.createElement('a');

    link.download = resolvedFileName;
    link.href = blobUrl;
    link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
      link.remove();
    }, 100);
  }
}
